import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface RequireAuthProps {
  children: React.ReactNode;
  roles?: string[]; // Optional array of roles that are allowed
}

/**
 * Protected route component that checks for authentication
 * and optionally checks for specific roles
 */
const RequireAuth: React.FC<RequireAuthProps> = ({ children, roles }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="auth-loading">
        <p>Checking authentication...</p>
        {/* Consider adding a loading spinner here */}
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!currentUser) {
    // Save the current location to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If roles are specified, check if user has required role
  if (roles && roles.length > 0) {
    const hasRequiredRole = roles.includes(currentUser.role) || currentUser.role === 'admin';

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // If all checks pass, render the children
  return <>{children}</>;
};

export default RequireAuth; 